import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Globe, ChevronDown, LogOut, Settings as SettingsIcon, Languages, Sun, Moon } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useMarket } from '../../../contexts/MarketContext';
import { useTheme } from '../../../contexts/ThemeContext';

// F1-F8 → route mapping (SAGAX 量化平台主要工作流)
// F1 智能为默认入口；原主页改名「行情」挂到 /overview；其他键顺移 +1。
const buildKeys = (t) => ([
  { id: 'F1', num: 'F1', label: t('termF1Label'), getRoute: () => '/sagax-agent' },
  { id: 'F2', num: 'F2', label: t('termF2Label'), getRoute: () => '/overview' },
  { id: 'F3', num: 'F3', label: t('termF3Label'), getRoute: () => '/data-docs' },
  { id: 'F4', num: 'F4', label: t('termF4Label'), getRoute: () => '/data-processing/factor' },
  { id: 'F5', num: 'F5', label: t('termF5Label'), getRoute: () => '/data-processing/comparison' },
  { id: 'F6', num: 'F6', label: t('termF6Label'), getRoute: () => '/strategy-matching' },
  { id: 'F7', num: 'F7', label: t('termF7Label'), getRoute: () => '/news-intelligence' },
  { id: 'F8', num: 'F8', label: t('termF8Label'), getRoute: () => '/agentic' },
]);

const routeToKey = (pathname) => {
  if (pathname.startsWith('/sagax-agent')) return 'F1';
  if (pathname === '/' || pathname === '' || pathname.startsWith('/overview')) return 'F2';
  if (pathname.startsWith('/data-docs') || pathname.startsWith('/api-docs')) return 'F3';
  if (pathname.startsWith('/data-processing/comparison')) return 'F5';
  if (pathname.startsWith('/data-processing')) return 'F4';
  // /macro 已合并进 F4 因子看板（?tab=macro），命中 F4
  if (pathname.startsWith('/strategy-matching')) return 'F6';
  if (pathname.startsWith('/news-intelligence')) return 'F7';
  if (pathname.startsWith('/agentic')) return 'F8';
  return null;
};

const CompactMarketSwitcher = () => {
  const { currentMarket, markets, switchMarket } = useMarket();
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  return (
    <div className="relative" ref={wrap}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="fkey"
        title="切换市场 / Switch market"
        style={{
          minWidth: 116,
          borderColor: 'var(--color-accent)',
          height: 24,
          fontSize: 11,
          fontWeight: 600,
          paddingLeft: 10,
          paddingRight: 10,
        }}
      >
        <Globe className="w-3 h-3" style={{ color: 'var(--color-accent)' }} />
        <span style={{ color: 'var(--color-accent)', letterSpacing: '0.04em' }}>{currentMarket.name}</span>
        <span className="term-muted">·</span>
        <span className="term-muted" style={{ fontSize: 10 }}>{currentMarket.nameEn}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: 'var(--color-accent)' }} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-px z-50 term-panel"
          style={{ minWidth: 140 }}
        >
          {markets.map(m => {
            const active = m.id === currentMarket.id;
            return (
              <button
                key={m.id}
                onClick={() => { setOpen(false); if (!active) switchMarket(m.id); }}
                className="w-full text-left px-3 py-1.5 term-mono"
                style={{
                  fontSize: 11,
                  color: active ? 'var(--color-accent)' : 'var(--color-text)',
                  backgroundColor: active ? 'var(--color-panelAlt)' : 'transparent',
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'var(--color-surfaceHover)'; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                {m.name} <span className="term-muted">({m.nameEn})</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const FunctionBar = () => {
  const { t, toggleLanguage, language } = useLanguage();
  const { themeName, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const keys = useMemo(() => buildKeys(t), [t]);
  const activeKey = routeToKey(location.pathname);

  // Global F1-F9 listener — skip when an editable element is focused
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target?.tagName || '').toLowerCase();
      const editable = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable;
      if (editable) return;
      const m = e.key && /^F([1-9])$/.exec(e.key);
      if (!m) return;
      const idx = parseInt(m[1], 10) - 1;
      const k = keys[idx];
      if (!k) return;
      e.preventDefault();
      navigate(k.getRoute());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [keys, navigate]);

  return (
    <div
      className="flex items-center justify-between"
      style={{
        height: 32,
        padding: '0 8px',
        backgroundColor: 'var(--color-statusBg, #0F0F0F)',
        borderBottom: '1px solid var(--color-border, #2A2A2A)',
      }}
    >
      <div className="flex items-center gap-1 fkey-bar" style={{ overflowX: 'auto', overflowY: 'hidden', minWidth: 0, flex: 1 }}>
        <div
          className="term-uimono"
          style={{ color: 'var(--color-accent)', fontSize: 12, fontWeight: 700, letterSpacing: '0.16em', paddingRight: 12, flexShrink: 0 }}
        >
          SAGAX
        </div>
        {keys.map(k => (
          <button
            key={k.id}
            type="button"
            onClick={() => navigate(k.getRoute())}
            className={`fkey ${activeKey === k.id ? 'fkey-active' : ''}`}
            title={k.label}
          >
            <span className="fkey-num">{k.num}</span>
            <span>{k.label}</span>
          </button>
        ))}
      </div>

      <div className="flex items-center gap-1">
        <button type="button" onClick={toggleLanguage} className="fkey" title="Language">
          <Languages className="w-3 h-3" />
          <span>{language === 'zh' ? 'ZH' : 'EN'}</span>
        </button>
        <button
          type="button"
          onClick={toggleTheme}
          className="fkey"
          title={themeName === 'dark' ? '切换到白天模式 / Switch to light' : '切换到黑夜模式 / Switch to dark'}
        >
          {themeName === 'dark' ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
        </button>
        <button type="button" onClick={() => navigate('/settings')} className="fkey" title="Settings">
          <SettingsIcon className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={() => {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }}
          className="fkey"
          title="Sign out"
        >
          <LogOut className="w-3 h-3" />
        </button>
        <span
          style={{
            display: 'inline-block',
            width: 1,
            height: 18,
            background: 'var(--color-borderStrong, #3D3D3D)',
            margin: '0 6px',
          }}
        />
        <CompactMarketSwitcher />
      </div>
    </div>
  );
};

export default FunctionBar;
